import React, { useEffect, useState, useCallback } from "react";
import axiosInstance from "../../../../components/axiosInstance";
import { API_URLS } from "../../../../apiUrl";
import Pagination from "@mui/material/Pagination";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

interface CsvUploadHistory {
  id: number;
  file_path: string;
  status: string;
  entries_count: number;
  successful_entry_count: number;
  failed_entry_count: number;
  approx_execution_time: string;
  created_at: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  lastPage: number;
  totalValues: number;
}

const formatDate = (dateString: string | null) => {
  if (!dateString) return "—";
  try {
    const date = new Date(dateString);
    return date
      .toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true, // set false if you want 24-hour format
      })
      .replace(/\//g, "-");
  } catch {
    return "Invalid Date";
  }
};

const CsvUploadStatus = () => {
  const [rows, setRows] = useState<CsvUploadHistory[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 50,
    lastPage: 1,
    totalValues: 0,
  });
  const [loading, setLoading] = useState(false);

  const fetchUploadStatus = useCallback(async (page: number) => {
    setLoading(true);

    try {
      const response = await axiosInstance.post(API_URLS.uploadcsvhistory, {
        page,
        limit: 50,
      });

      const data = response.data;

      if (data.error) {
        toast.error(data.message || "Failed to fetch upload status");
        return;
      }

      setRows(data?.data?.rows ?? []);
      setPagination(
        data?.data?.pagination ?? {
          page: 1,
          limit: 50,
          lastPage: 1,
          totalValues: 0,
        }
      );
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUploadStatus(1);
  }, [fetchUploadStatus]);

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    fetchUploadStatus(page);
  };

  return (
    <div className="w-full px-4 py-6 sm:px-6">
      <ToastContainer position="top-right" autoClose={4000} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100">
            <svg
              className="w-4 h-4 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-800">CSV Upload History</h1>
            <p className="text-[10px] text-gray-400 mt-0.5">
              {pagination.totalValues} total upload entries found
            </p>
          </div>
        </div>

        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-600 border border-blue-200 w-fit">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
          Upload Log
        </span>
      </div>

      {/* Table */}
      <div className="w-full rounded-xl border border-gray-200 shadow-sm bg-white overflow-x-auto">
        <table className="min-w-full border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="py-2 px-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Sl. No.
              </th>
              <th className="py-2 px-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                File Name
              </th>
              <th className="py-2 px-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Status
              </th>
              <th className="py-2 px-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Total Entries
              </th>
              <th className="py-2 px-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Success
              </th>
              <th className="py-2 px-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Failed
              </th>
              <th className="py-2 px-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Execution Time
              </th>
              <th className="py-2 px-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Uploaded Date
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="animate-pulse">
                  {Array.from({ length: 8 }).map((__, j) => (
                    <td key={j} className="py-2 px-3">
                      <div className="h-2.5 bg-gray-100 rounded w-full" />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-12 text-center">
                  <div className="flex flex-col items-center gap-2 text-gray-400">
                    <svg
                      className="w-8 h-8 text-gray-200"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <p className="text-xs font-medium">No upload history found</p>
                    <p className="text-[10px]">No CSV files have been uploaded yet</p>
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => (
                <tr
                  key={row.id}
                  className={`hover:bg-blue-50/40 transition-colors duration-150 ${
                    idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                  }`}
                >
                  {/* Sl. No. */}
                  <td className="py-2 px-3 whitespace-nowrap">
                    <span className="text-[12px] font-mono text-gray-600">
                      {(pagination.page - 1) * pagination.limit + idx + 1}
                    </span>
                  </td>

                  {/* File Name */}
                  <td className="py-2 px-3 whitespace-nowrap">
                    <span className="text-[11px] font-medium text-gray-800">
                      {row.file_path?.split("/").pop() ?? (
                        <span className="text-gray-300">—</span>
                      )}
                    </span>
                  </td>

                  {/* Status */}
                  <td className="py-2 px-3 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold border ${
                        row.status === "failed" || row.status === "error"
                          ? "bg-red-100 text-red-700 border-red-200"
                          : row.status === "processing"
                          ? "bg-yellow-100 text-yellow-700 border-yellow-200"
                          : "bg-green-100 text-green-700 border-green-200"
                      }`}
                    >
                      <span
                        className={`w-1 h-1 rounded-full ${
                          row.status === "failed" || row.status === "error"
                            ? "bg-red-500"
                            : row.status === "processing"
                            ? "bg-yellow-500"
                            : "bg-green-500"
                        }`}
                      />
                      {row.status}
                    </span>
                  </td>

                  {/* Total Entries */}
                  <td className="py-2 px-3 whitespace-nowrap">
                    <span className="text-[11px] text-gray-600">
                      {row.entries_count ?? <span className="text-gray-300">—</span>}
                    </span>
                  </td>

                  {/* Success */}
                  <td className="py-2 px-3 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-green-100 text-green-700 border border-green-200">
                      <span className="w-1 h-1 rounded-full bg-green-500" />
                      {row.successful_entry_count}
                    </span>
                  </td>

                  {/* Failed */}
                  <td className="py-2 px-3 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-red-100 text-red-700 border border-red-200">
                      <span className="w-1 h-1 rounded-full bg-red-500" />
                      {row.failed_entry_count}
                    </span>
                  </td>

                  {/* Execution Time */}
                  <td className="py-2 px-3 whitespace-nowrap">
                    <span className="text-[11px] text-gray-600">
                      {row.approx_execution_time ?? (
                        <span className="text-gray-300">—</span>
                      )}
                    </span>
                  </td>

                  {/* Uploaded Date */}
                  <td className="py-2 px-3 whitespace-nowrap">
                    <span className="text-[11px] text-gray-600">
                      {formatDate(row.created_at)}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
        <div className="mt-4 flex justify-center">
          <Pagination
            count={pagination.lastPage}
            page={pagination.page}
            onChange={handlePageChange}
            color="primary"
            size="small"
            shape="rounded"
            siblingCount={1}
            boundaryCount={1}
          />
        </div>
 
    </div>
  );
};

export default CsvUploadStatus;